import { useDeliveryApi } from 'infrastructure/api';
import { ResourceData, useCrudService } from 'framework/crud';
import { cloneDeep } from 'lodash';
import {
  customerReportRejectValidation,
  customerReportValidation,
  externalReportValidation,
  recipientsValidation,
  sendReportValidation,
} from './customerReportValidation';

const useCustomerReportService = () => {
  const singleResourceUrl = '/projects/:id';
  const {
    search: baseSearch,
    update: initialUpdate,
    ...baseCrudRequests
  } = useCrudService({
    singleResourceUrl,
    listResourceUrl: '/projects',
    useService: useDeliveryApi,
  });

  const { sendGetRequest, sendPostRequest, sendPutRequest } = useDeliveryApi();

  const contentReportSearch = async (
    projectId: string,
    isSystemReportUsed: Boolean,
    request: ResourceData,
  ): Promise<any> => {
    const searchRequest = cloneDeep(request);
    const response = await sendPostRequest(`/projects/${projectId}/customer-reports/content`, {
      isSystemReportUsed,
      searchRequest,
    });
    return response.json();
  };

  const batchRecipients = async (projectId: string, request: ResourceData): Promise<any> => {
    const response = await sendPostRequest(`/projects/${projectId}/report-recipients/batch`, request);
    return response.json();
  };

  const customerReportSearch = async (request: ResourceData): Promise<any> => {
    const searchParams = cloneDeep(request);
    const response = await sendPostRequest(`/projects/{projectId}/customer-reports/search`, searchParams);
    return response.json();
  };

  const getCustomerReportById = async (id: string, request: ResourceData): Promise<any> => {
    const response = await sendGetRequest(`/projects/customer-reports/${id}`, request);
    return response.json();
  };
  const approveCustomerReport = async (id: string, request: ResourceData): Promise<any> => {
    const response = await sendPutRequest(`/projects/customer-reports/${id}/approve`, request);
    return response.json();
  };

  const rejectCustomerReport = async (id: string, rejectionReason: string): Promise<any> => {
    const response = await sendPutRequest(`/projects/customer-reports/${id}/reject`, { rejectionReason });
    return response.json();
  };

  const submitCustomerReport = async (id: string, request: ResourceData): Promise<any> => {
    const response = await sendPostRequest(`/projects/${id}/customer-reports/submit`, request);
    return response.json();
  };

  const cancelCustomerReport = async (id: string, request: ResourceData): Promise<any> => {
    const response = await sendPutRequest(`/projects/customer-reports/${id}/cancel`, request);
    return response.json();
  };

  const createDraftReport = async (projectId: string, request: ResourceData): Promise<any> => {
    const response = await sendPostRequest(`/projects/${projectId}/customer-reports/draft`, request);
    return response.json();
  };
  const updateDraftReport = async (reportId: string, request: ResourceData): Promise<any> => {
    const response = await sendPutRequest(`/projects/customer-reports/${reportId}/draft`, request);
    return response.json();
  };

  const saveExternalReport = async (projectId: string, request: ResourceData): Promise<any> => {
    const response = await sendPostRequest(`/projects/${projectId}/customer-reports/external`, request);

    return response.json();
  };
  const updateExternalReport = async (id: string, request: ResourceData): Promise<any> => {
    const response = await sendPutRequest(`/projects/customer-reports/${id}/external`, request);

    return response.json();
  };

  const sendSyberryReport = async (id: string, request: ResourceData): Promise<any> => {
    const response = await sendPostRequest(`/projects/customer-reports/${id}/send`, request);
    return response.json();
  };

  const sendDirectReport = async (id: string, request: ResourceData): Promise<any> => {
    const response = await sendPostRequest(`/projects/${id}/customer-reports/send-direct`, request);
    return response.json();
  };

  const recipientsSearch = async (projectId: string, request: ResourceData): Promise<any> => {
    const searchParams = cloneDeep(request);
    const response = await sendPostRequest(`/projects/${projectId}/report-recipients/search`, searchParams);
    return response.json();
  };

  const recipientsStatusBatch = async (projectId: string, data: ResourceData): Promise<any> => {
    const response = await sendPutRequest(`/projects/${projectId}/report-recipients/status`, data);
    return response.json();
  };

  const getCustomerReportRejectValidationSchema = () => Promise.resolve(customerReportRejectValidation);
  const getCustomerReportValidationSchema = () => Promise.resolve(customerReportValidation);
  const getExternalReportValidationSchema = () => Promise.resolve(externalReportValidation);
  const getSendReportValidationSchema = () => Promise.resolve(sendReportValidation);
  const getRecipientsValidationSchema = () => Promise.resolve(recipientsValidation);

  return {
    ...baseCrudRequests,
    contentReportSearch,
    customerReportSearch,
    getCustomerReportById,
    submitCustomerReport,
    approveCustomerReport,
    createDraftReport,
    updateDraftReport,
    saveExternalReport,
    rejectCustomerReport,
    cancelCustomerReport,
    sendDirectReport,
    sendSyberryReport,
    recipientsSearch,
    batchRecipients,
    updateExternalReport,
    recipientsStatusBatch,
    getCustomerReportRejectValidationSchema,
    getExternalReportValidationSchema,
    getSendReportValidationSchema,
    getRecipientsValidationSchema,
    getCustomerReportValidationSchema,
  };
};

export default useCustomerReportService;
